/**
 * ehr#437 part 3 (PROGRESS_NOTE_10-14-V81 practicePresetSeed, CASE_MANAGEMENT_NOTES_10-14-V25
 * DRAWER_PRESETS): which parts a note shows, in what order and under what names, by the kind of
 * note. The note's own sections (SOAP / DAP / BIRP) are the base; a layout hides, renames, moves
 * and adds parts. Pure, so the editor, the sign-off check and the PDF all read one list.
 */
import { FORMAT_SECTIONS_V81 } from './noteSections.js';
import { PEER_ACTIVITIES, PEER_ACTIVITIES_ID, PEER_ACTIVITIES_LABEL, PEER_SUPPORT_KIND, RECOVERY_GOAL_ID, RECOVERY_GOAL_LABEL, } from './peerSupport.js';
import { NO_SHOW_FLAGS, NO_SHOW_FLAGS_ID, NO_SHOW_FLAGS_LABEL, NO_SHOW_KIND, } from './noShowNote.js';
/** No billing code and no diagnosis; the note boxes are optional (V81 NO_CODE_TYPES). */
export const NO_CODE_KINDS = ['No-show / cancellation', 'Admin / phone'];
/** Two-box Service notes (V81 SERVICE_TYPES). */
export const SERVICE_KINDS = [
    'Case management',
    'Peer support',
    'Skills / rehab',
    'Collateral contact',
];
/** The "Other notes" kinds (CASE_MANAGEMENT_NOTES DRAWER_TYPES): SOAP base, no template choice. */
export const OTHER_KINDS = [
    ...SERVICE_KINDS,
    'New Client',
    'Medication management',
    'Treatment plan review',
    'Discharge summary',
    'Psych testing',
    ...NO_CODE_KINDS,
];
const has = (list, kind) => list.includes(kind);
/** Safety isn't asked on Service and Brief contact notes (V81 safetyOff). */
export const safetyOff = (kind) => has(NO_CODE_KINDS, kind) || has(SERVICE_KINDS, kind);
export const isOtherKind = (kind) => has(OTHER_KINDS, kind);
/** ehr#708 (V6): kinds with no Problems, goals, and objectives addressed section. */
export const FOCUS_OFF_KINDS = ['Discharge summary'];
const MENTAL_STATUS = [
    'Alert and oriented x3',
    'Appearance appropriate',
    'Speech normal rate and volume',
    'Mood and affect congruent',
    'Thought process logical',
    'Insight and judgment fair',
];
const SAFETY_PLAN_REVIEW = [
    'Reviewed warning signs',
    'Reviewed coping strategies',
    'Confirmed support contacts',
    'Confirmed crisis line numbers',
    'Means safety discussed',
];
const hide = (...ids) => ids.map((id) => ({ type: 'hide', id }));
const rename = (id, label, hint) => ({
    type: 'rename',
    id,
    label,
    hint,
});
// Other notes have no Interventions used or Client response (CASE_MANAGEMENT_NOTES withInterItems).
const OTHER_HIDES = hide('interventions', 'response');
/** The layouts by kind, in V81's words. */
const LAYOUTS = [
    {
        kinds: ['Crisis'],
        ops: [
            { type: 'move', id: 'safety', after: 'session' },
            {
                type: 'add',
                after: 'safety',
                block: {
                    id: 'safetyPlanReview',
                    kind: 'checklist',
                    label: 'Safety plan review',
                    items: SAFETY_PLAN_REVIEW,
                },
            },
            { type: 'collapse', id: 'goals' },
        ],
    },
    {
        kinds: ['New Client'],
        ops: [
            {
                type: 'add',
                after: 'session',
                block: {
                    id: 'presentingProblem',
                    kind: 'box',
                    label: 'Presenting problem',
                    hint: 'Why the client is seeking care now.',
                },
            },
            {
                type: 'add',
                after: 'presentingProblem',
                block: {
                    id: 'relevantHistory',
                    kind: 'box',
                    label: 'Relevant history',
                    hint: 'Psychiatric, medical, family and social history.',
                },
            },
            {
                type: 'add',
                after: 'relevantHistory',
                block: {
                    id: 'mentalStatus',
                    kind: 'checklist',
                    label: 'Mental status',
                    items: MENTAL_STATUS,
                },
            },
            { type: 'move', id: 'dx', after: 'mentalStatus' },
            ...hide('subjective', 'objective', 'assessment', 'goals'),
            ...OTHER_HIDES,
        ],
    },
    {
        kinds: SERVICE_KINDS,
        ops: [
            rename('subjective', 'What happened', 'The service given, who was there and what came of it.'),
            rename('plan', 'Next step', 'What happens next and when.'),
            ...hide('objective', 'assessment', 'goals'),
            ...OTHER_HIDES,
        ],
    },
    {
        // ehr#727 (PEER_SUPPORT_NOTE_10-30-V8, 12a and 11a): Recovery goal, then Peer support
        // activities, right above What happened. Both optional; the editor gives the activities the
        // practice's own list.
        kinds: [PEER_SUPPORT_KIND],
        ops: [
            {
                type: 'addBefore',
                before: 'subjective',
                block: {
                    id: RECOVERY_GOAL_ID,
                    kind: 'box',
                    label: RECOVERY_GOAL_LABEL,
                    hint: "In the client's own words",
                },
            },
            {
                type: 'addBefore',
                before: 'subjective',
                block: {
                    id: PEER_ACTIVITIES_ID,
                    kind: 'checklist',
                    label: PEER_ACTIVITIES_LABEL,
                    items: PEER_ACTIVITIES,
                },
            },
        ],
    },
    {
        kinds: ['Medication management'],
        ops: [
            rename('subjective', 'Medication review', 'Medications, doses, adherence, side effects and response.'),
            ...hide('objective', 'assessment', 'goals'),
            ...OTHER_HIDES,
        ],
    },
    {
        kinds: ['Treatment plan review'],
        ops: [
            rename('subjective', 'Progress summary', 'Progress since the plan was signed.'),
            rename('plan', 'Plan changes', 'Goals, objectives or services to change.'),
            ...hide('objective', 'assessment'),
            { type: 'move', id: 'goals', after: 'subjective' },
            ...OTHER_HIDES,
        ],
    },
    {
        kinds: ['Discharge summary'],
        ops: [
            rename('subjective', 'Reason for discharge', 'Why services are ending.'),
            rename('assessment', 'Progress summary', 'Progress over the course of treatment.'),
            rename('plan', 'Aftercare and referrals', 'Referrals, resources and follow-up.'),
            ...hide('objective'),
            { type: 'move', id: 'goals', after: 'assessment' },
            ...OTHER_HIDES,
        ],
    },
    {
        kinds: ['Psych testing'],
        ops: [
            rename('subjective', 'Tests given', 'Tests given and time spent.'),
            rename('assessment', 'Results', 'Scores and what they show.'),
            rename('plan', 'Recommendations', 'Recommendations from the results.'),
            ...hide('objective', 'goals'),
            ...OTHER_HIDES,
        ],
    },
    {
        kinds: NO_CODE_KINDS,
        ops: [
            rename('subjective', 'Contact note', 'No-show reason, outreach made, or the admin task.'),
            ...hide('objective', 'assessment', 'plan', 'goals', 'dx'),
            ...OTHER_HIDES,
        ],
    },
];
/**
 * ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V9, 20 A to 22 A): the kinds whose plan goals are a practice
 * setting (Practice note settings > Show treatment plan goals, caseManagementGoals), off by default.
 */
export const GOALS_SETTING_KINDS = ['Case management'];
export const goalsSettingKind = (kind) => has(GOALS_SETTING_KINDS, kind);
/**
 * The note's parts in order for its template and kind. Other notes always use the SOAP base
 * (their layouts rename its boxes). A Credify Note has its own fields: only the fixed parts here.
 * ehr#725: `planGoals` (the practice's Show treatment plan goals) keeps Treatment plan goals, and
 * so Problems, goals, and objectives addressed, on a GOALS_SETTING_KINDS note.
 * `absent`: an absent group or family member's note, written on the no-show layout (ehr#731: it
 * keeps the Contact note optional and has no Flags).
 */
export function noteBlocks(format, kind, opts = {}) {
    const fmt = isOtherKind(kind) ? 'SOAP' : format;
    const keepGoals = !!opts.planGoals && goalsSettingKind(kind);
    const boxesOptional = has(NO_CODE_KINDS, kind);
    // A Credify Note's Objective and Assessment are part of its own fields (CredifyNoteFields).
    const text = fmt === 'CREDIFY' ? [] : (FORMAT_SECTIONS_V81[fmt] ?? []);
    let out = [
        { id: 'session', kind: 'session', label: 'Session details' },
        { id: 'goals', kind: 'goals', label: 'Treatment plan goals' },
        { id: 'dx', kind: 'dx', label: 'Diagnosis', required: true },
    ];
    for (const [label, key, hint] of text) {
        if (key === 'response' && fmt !== 'BIRP')
            out.push({
                id: 'interventions',
                kind: 'interventions',
                label: 'Interventions used',
                required: true,
            });
        out.push({ id: key, kind: 'text', label, hint, required: !boxesOptional });
    }
    if (!safetyOff(kind) && fmt !== 'CREDIFY')
        out.push({ id: 'safety', kind: 'safety', label: 'Safety' });
    for (const l of LAYOUTS) {
        if (!has(l.kinds, kind))
            continue;
        for (const op of l.ops) {
            const i = out.findIndex((b) => b.id === ('id' in op ? op.id : ''));
            if (op.type === 'hide') {
                if (i >= 0 && !(keepGoals && op.id === 'goals'))
                    out = out.filter((_, j) => j !== i);
            }
            else if (op.type === 'rename') {
                if (i >= 0)
                    out[i] = { ...out[i], label: op.label, hint: op.hint };
            }
            else if (op.type === 'collapse') {
                if (i >= 0)
                    out[i] = { ...out[i], collapsed: true };
            }
            else if (op.type === 'move') {
                if (i < 0)
                    continue;
                const [b] = out.splice(i, 1);
                out.splice(out.findIndex((x) => x.id === op.after) + 1, 0, b);
            }
            else if (op.type === 'addBefore') {
                const at = out.findIndex((x) => x.id === op.before);
                out.splice(at < 0 ? out.length : at, 0, op.block);
            }
            else {
                out.splice(out.findIndex((x) => x.id === op.after) + 1, 0, op.block);
            }
        }
    }
    // ehr#731 (Jay 10-30 on #713): a no-show's Contact note must be written, and its Flags (Risk
    // concern, Leading to discharge) follow it
    if (kind === NO_SHOW_KIND && !opts.absent) {
        const c = out.findIndex((b) => b.id === 'subjective');
        if (c >= 0) {
            out[c] = { ...out[c], required: true };
            out.splice(c + 1, 0, {
                id: NO_SHOW_FLAGS_ID,
                kind: 'checklist',
                label: NO_SHOW_FLAGS_LABEL,
                items: NO_SHOW_FLAGS,
            });
        }
    }
    // ehr#682 (V141): Problems, goals, and objectives addressed follows Treatment plan goals,
    // wherever the layout has that part. ehr#708 (DISCHARGE_SUMMARY_NOTE_10-30-V6 QA block 1): not
    // on a discharge summary, which gives every goal a final status instead.
    const g = out.findIndex((b) => b.id === 'goals');
    if (g >= 0 && !has(FOCUS_OFF_KINDS, kind))
        out.splice(g + 1, 0, {
            id: 'focus',
            kind: 'focus',
            label: 'Problems, goals, and objectives addressed',
        });
    return out;
}
/** A Quick add rating's name on the note ("Mood rating"). */
export const quickLabel = (q) => q.group === 'ratings' && !/rating$/i.test(q.label) ? `${q.label} rating` : q.label;
/**
 * ehr#437 (V81 Quick add, quickOps): the note's Quick add sections put into its layout. Ratings go
 * after Session details; the rest after Plan, Assessment, the first box, or the box phrases go in
 * (Objective; Data on DAP; Intervention on BIRP). Several after one part keep the order added.
 * A part the layout doesn't have falls back to the last box.
 */
export function withQuick(blocks, quick, format) {
    if (!quick?.length)
        return blocks;
    const out = [...blocks];
    const texts = () => out.filter((b) => b.kind === 'text' && !b.quick);
    const phraseBox = format === 'BIRP' ? 'intervention' : format === 'DAP' ? 'data' : 'objective';
    const last = {};
    for (const q of quick) {
        if (out.some((b) => b.id === q.id))
            continue;
        let anchor;
        if (q.group === 'ratings')
            anchor = 'session';
        else if (q.after === '@first')
            anchor = texts()[0]?.id;
        else if (q.after === '@phrase')
            anchor = out.some((b) => b.id === phraseBox) ? phraseBox : texts()[0]?.id;
        else
            anchor = q.after ?? 'plan';
        if (!anchor || !out.some((b) => b.id === anchor))
            anchor = texts().at(-1)?.id ?? out.at(-1)?.id;
        const key = anchor ?? '';
        const after = last[key] ?? anchor;
        const block = {
            id: q.id,
            kind: q.group === 'ratings' ? 'rating' : q.group === 'checklists' ? 'checklist' : 'box',
            label: quickLabel(q),
            quick: true,
            ...(q.items ? { items: q.items } : {}),
            ...(q.group === 'ratings' ? { min: q.min ?? 0, max: q.max ?? 10 } : {}),
        };
        out.splice(out.findIndex((b) => b.id === after) + 1, 0, block);
        last[key] = q.id;
    }
    return out;
}
//# sourceMappingURL=noteLayouts.js.map